import { useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { parseRouteLabel } from "../../../utils/router/parseRouteLabel/parseRouteLabel";
import { ReactComponent as ArrowUpRight } from "../../../assets/vectors/icons/arrow-up-right.svg";
import logo from "../../../assets/vectors/logo/applia_logo_white.svg";
import { SocialRow } from "../SocialRow/SocialRow";
import { UiColors } from "../../../config/ui/colors";
import { APPLIA_WEBSITE_URL } from "../../../config/env";

import { SidebarCollapsible } from "./SidebarCollapsible/SidebarCollapsible";
import { SidebarItem } from "./SidebarItem/SidebarItem";
import { SidebarProps } from "./Sidebar.type";
import { useSidebarScrollPositionOnPageLoad } from "./hooks/useSidebarScrollPositionOnPageLoad";

const Sidebar = ({ routes, mobile = false }: SidebarProps) => {
	const location = useLocation();

	const { itemsRef } = useSidebarScrollPositionOnPageLoad(routes, mobile);

	// JS Map is used for storing sidebar link refs
	// (used for scrolling it in to view after page refresh)
	const getMap = useCallback(() => {
		if (!itemsRef.current) {
			// Initialize the Map on first usage.
			itemsRef.current = new Map();
		}

		return itemsRef.current;
	}, [itemsRef]);

	return (
		<nav className="w-full h-full" aria-label="Sidebar">
			<div className="h-full px-3 py-4 overflow-y-auto overflow-x-hidden bg-[#00348a] flex flex-col [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-tertiary [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
				{!mobile && (
					<>
						<NavLink to="/" className="flex items-center flex-shrink-0 px-2 py-4">
							<img src={logo} className="w-28" alt="APPLiA logo" />
						</NavLink>

						<hr className="border-tertiary -mx-3 mt-4" />
					</>
				)}

				<NavLink to="/" className="px-2 py-4 block">
					<p className="text-primary text-lg">2025</p>
					<p className="text-white text-lg font-bold">Home Appliance Monitor</p>
				</NavLink>

				<hr className="border-tertiary -mx-3 mt-1" />

				{routes.map((route) => (
					<SidebarCollapsible
						key={route.label}
						label={parseRouteLabel(route, route.label)}
						title={route?.label ?? ""}
						active={location.pathname === route?.url}
						disabled={!!route.disabled}
						url={route?.url}
						ref={(node) => {
							const map = getMap();

							if (node && !mobile) {
								map.set(route?.label, node);
							} else {
								map.delete(route?.label);
							}
						}}
					>
						{Array.isArray(route?.children)
							? route.children.map(({ url = "", label }) => (
									<SidebarItem
										ref={(node) => {
											const map = getMap();

											if (node && !mobile) {
												map.set(label, node);
											} else {
												map.delete(label);
											}
										}}
										key={label}
										id={label}
										title={label}
										to={route?.url ? route?.url + url : route?.pathName ? route?.pathName + url : url}
									>
										{label}
									</SidebarItem>
							  ))
							: null}
					</SidebarCollapsible>
				))}
				<div className="mt-auto space-y-2 p-2 pt-4">
					<hr className="border-tertiary -mx-5 mb-4" />
					<p className="text-white">Follow us</p>
					<SocialRow />
					<a className="pt-6 text-base flex items-center gap-1 text-white" target="_blank" href={APPLIA_WEBSITE_URL} rel="noreferrer">
						{"Visit APPLiA website"}
						<ArrowUpRight width={20} height={20} color={UiColors.white} />
					</a>
					<p className="text-tertiary text-sm">APPLiA &copy; 2026</p>
				</div>
			</div>
		</nav>
	);
};

export { Sidebar };
